// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ChainUptimeStore, useChainUptimeQuery } from "@/api/hooks/use-chain-uptime";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { DatePicker } from "@/components/shared/date-picker";
import { FilterSelect } from "@/components/shared/filter-select";
import { KeyChip } from "@/components/shared/key-chip";
import { StoreLink } from "@/components/shared/store-link";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { formatUptimePercent, UPTIME_GRADIENT, uptimeSwatch } from "./uptime-scale";

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

// Floor width of one hour's band, and the gap between two of them. Together they set the
// strip's minimum: below this the bands stop being distinguishable and the column is
// better off forcing the table to scroll.
const BAND_MIN_PX = 10;
const BAND_GAP_PX = 1;
const STRIP_MIN_PX = HOURS.length * BAND_MIN_PX + (HOURS.length - 1) * BAND_GAP_PX;

// Every third hour is labelled on the axis. Labelling all 24 collides as soon as the
// column is narrow, and a tick every three hours is still enough to place a band by eye.
const AXIS_LABEL_EVERY = 3;

// Ids the table can actually sort by. A URL saved before the per-hour columns were folded
// into one strip still carries `hour-7`, and handing that to the table means looking up a
// column that no longer exists.
const SORTABLE_COLUMNS = new Set(["legibleName", "storeName", "regionName", "cameraCount", "average"]);

// The table opens on the day's average, best first. Kept in one place because it seeds the
// sort state, the URL defaults and the sort itself — and a mismatch between them would
// encode the default into every URL as though it were a choice the user had made.
const DEFAULT_SORT = { id: "average", desc: true };

// Offered as "show me stores that dipped below X at some point in the day". 100 is the
// first entry because "anything less than perfect" is the question most often asked of
// this table; the lower bands narrow it to stores worth paging someone about.
const THRESHOLDS = [100, 95, 90, 75, 50];

interface ChainUptimeTabProps {
  chainName: string;
  initialFilters?: string;
}

const FILTER_DEFAULTS = {
  search: "",
  region: "",
  threshold: "",
  date: "",
  sortId: DEFAULT_SORT.id,
  sortDesc: DEFAULT_SORT.desc,
};

/** Mean uptime across the hours that had anything due. Null when the day had none. */
function dayAverage(store: ChainUptimeStore): number | null {
  const measured = store.hours.filter((hour) => hour.uptime != null);
  if (measured.length === 0) return null;
  return measured.reduce((sum, hour) => sum + (hour.uptime ?? 0), 0) / measured.length;
}

export function ChainUptimeTab({ chainName, initialFilters }: ChainUptimeTabProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const initial = useMemo(() => decodeFilters(initialFilters, FILTER_DEFAULTS), [initialFilters]);

  const [sorting, setSorting] = useState<SortingState>([
    { id: SORTABLE_COLUMNS.has(initial.sortId) ? initial.sortId : DEFAULT_SORT.id, desc: initial.sortDesc },
  ]);
  const [search, setSearch] = useState(initial.search);
  const [regionFilter, setRegionFilter] = useState(initial.region);
  const [threshold, setThreshold] = useState(initial.threshold);
  const [date, setDate] = useState<string | undefined>(initial.date || undefined);
  // The whole chain-day arrives in one response, so the table never pages. Pagination
  // state still has to exist because `DataTable` owns a paginated table's machinery
  // either way — it just goes unused with `paginated={false}`.
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 0 });

  const { data, isLoading, isError, refetch, isFetching } = useChainUptimeQuery(chainName, date);
  const stores = useMemo(() => data?.stores ?? [], [data]);

  const isFiltered = !!(search || regionFilter || threshold);

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const encoded = encodeFilters(
        {
          search,
          region: regionFilter,
          threshold,
          date: date ?? "",
          sortId: sorting[0]?.id ?? DEFAULT_SORT.id,
          sortDesc: sorting[0]?.desc ?? DEFAULT_SORT.desc,
          ...overrides,
        },
        FILTER_DEFAULTS,
      );
      navigate({
        to: "/chains/$chainName",
        params: { chainName },
        search: { t: "uptime", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [search, regionFilter, threshold, date, sorting, navigate, chainName],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setRegionFilter("");
    setThreshold("");
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters });

  // Regions come off the rows rather than the shared backoffice context: this table only
  // lists stores that have installed cameras, so offering a region with nothing in it
  // would filter the table to empty.
  const availableRegions = useMemo(() => {
    const names = new Set<string>();
    for (const store of stores) if (store.regionName) names.add(store.regionName);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [stores]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const floor = threshold ? Number(threshold) / 100 : null;
    const filtered = stores.filter((store) => {
      if (regionFilter && store.regionName !== regionFilter) return false;
      if (floor != null && !store.hours.some((hour) => hour.uptime != null && hour.uptime < floor)) return false;
      if (!needle) return true;
      return (
        store.legibleName.toLowerCase().includes(needle) ||
        store.storeName.toLowerCase().includes(needle) ||
        store.timeZone.toLowerCase().includes(needle) ||
        (store.regionName ?? "").toLowerCase().includes(needle) ||
        String(store.storeId) === needle
      );
    });

    const { id, desc } = sorting[0] ?? DEFAULT_SORT;
    // A store with no measured hours sorts last in either direction — an absence of data
    // is not a bad day, and burying it under the worst outages would read as one.
    const key = (store: ChainUptimeStore): string | number | null => {
      if (id === "average") return dayAverage(store);
      if (id === "cameraCount") return store.cameraCount;
      if (id === "regionName") return store.regionName ?? null;
      if (id === "storeName") return store.storeName;
      return store.legibleName;
    };
    return [...filtered].sort((a, b) => {
      const left = key(a);
      const right = key(b);
      if (left == null && right == null) return 0;
      if (left == null) return 1;
      if (right == null) return -1;
      const order =
        typeof left === "number" && typeof right === "number"
          ? left - right
          : String(left).localeCompare(String(right));
      return desc ? -order : order;
    });
  }, [stores, search, regionFilter, threshold, sorting]);

  const handleExport = useCallback(async () => {
    downloadCsv(
      `chain-${chainName}-uptime-${data?.date ?? ""}.csv`,
      [
        t("chains.uptime.columns.id"),
        t("chains.uptime.columns.store"),
        t("chains.uptime.columns.storeName"),
        t("chains.uptime.columns.region"),
        t("chains.uptime.columns.timezone"),
        t("chains.uptime.columns.cameras"),
        t("chains.uptime.columns.average"),
        ...HOURS.map((hour) => String(hour).padStart(2, "0")),
      ],
      rows.map((store) => {
        const average = dayAverage(store);
        return [
          String(store.storeId),
          store.legibleName,
          store.storeName,
          store.regionName ?? "",
          store.timeZone,
          String(store.cameraCount),
          average == null ? "" : average.toFixed(4),
          // Blank, not 0 — an hour with nothing due has no uptime to report, and a 0 in a
          // spreadsheet cell would be averaged as an outage by whoever opens it.
          ...store.hours.map((hour) => (hour.uptime == null ? "" : hour.uptime.toFixed(4))),
        ];
      }),
    );
  }, [chainName, data?.date, rows, t]);

  const columns = useMemo<DataTableColumnDef<ChainUptimeStore>[]>(() => {
    const identity: DataTableColumnDef<ChainUptimeStore>[] = [
      {
        id: "legibleName",
        accessorKey: "legibleName",
        header: t("chains.uptime.columns.store"),
        enableHiding: false,
        // Deliberately no `forceWidth`: that pins width, min and max to one number, and a
        // chain whose names run long than gets them clipped rather than the column widened.
        // Cells are `whitespace-nowrap`, so auto layout sizes this to the longest name and
        // the strip — which has a floor of its own — absorbs whatever is left.
        meta: { columnLabel: t("chains.uptime.columns.store"), className: "pl-4", sticky: "left" },
        cell: ({ row }) => (
          <div data-slot="uptime-store" data-store={row.original.legibleName}>
            <StoreLink
              storeName={row.original.storeName}
              legibleName={row.original.legibleName}
              className="font-medium"
            />
          </div>
        ),
      },
      {
        id: "storeName",
        accessorKey: "storeName",
        header: t("chains.uptime.columns.storeName"),
        meta: { columnLabel: t("chains.uptime.columns.storeName"), defaultHidden: true },
        cell: ({ row }) => <KeyChip label={row.original.storeName} variant="store" />,
      },
      {
        id: "regionName",
        accessorKey: "regionName",
        header: t("chains.uptime.columns.region"),
        meta: { columnLabel: t("chains.uptime.columns.region"), defaultHidden: true },
        cell: ({ row }) => row.original.regionName ?? "—",
      },
      {
        id: "cameraCount",
        accessorKey: "cameraCount",
        header: t("chains.uptime.columns.cameras"),
        meta: { columnLabel: t("chains.uptime.columns.cameras"), defaultHidden: true },
        cell: ({ row }) => <span className="block text-right tabular-nums">{row.original.cameraCount}</span>,
      },
      {
        id: "average",
        accessorFn: dayAverage,
        header: t("chains.uptime.columns.average"),
        meta: { columnLabel: t("chains.uptime.columns.average") },
        cell: ({ row }) => {
          const average = dayAverage(row.original);
          return <span className="block text-right tabular-nums">{average == null ? "—" : average.toFixed(2)}</span>;
        },
      },
    ];

    const strip: DataTableColumnDef<ChainUptimeStore> = {
      id: "uptime",
      header: () => <UptimeAxis />,
      // A display column with no accessor, which is also what makes it unsortable — there
      // is no single value for 24 hours to sort on, and Average is the column for that.
      enableSorting: false,
      // `w-full` is what makes the strip the column that grows. Auto table layout hands
      // surplus width to the widest column by default, which left the store names sitting
      // in a sea of whitespace while the strip was squeezed; a 100% width preference on
      // this cell claims the slack instead and collapses every other column onto its own
      // content. The strip's own min-width remains the floor below which the table scrolls.
      meta: { columnLabel: t("chains.uptime.columns.uptime"), className: "w-full px-2" },
      cell: ({ row }) => <UptimeStrip hours={row.original.hours} />,
    };

    return [...identity, strip];
  }, [t]);

  if (isError) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {t("errors.loadFailed")}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <DataTable
        storageKey="chain-uptime"
        columns={columns}
        data={rows}
        totalCount={rows.length}
        isLoading={isLoading}
        paginated={false}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        search={search}
        onSearchChange={setSearch}
        searchDebounceMs={200}
        onRefresh={refetch}
        isRefreshing={isFetching}
        onExport={handleExport}
        searchPlaceholder={t("chains.uptime.searchPlaceholder")}
        footerNote={<UptimeLegend />}
        filters={
          <>
            <div className="w-44">
              <DatePicker
                value={date}
                onChange={(value) => setDate(value)}
                placeholder={t("chains.uptime.today")}
                disableAfter={new Date()}
              />
            </div>
            {availableRegions.length > 0 && (
              <FilterSelect
                label={t("chains.uptime.filter.region")}
                value={regionFilter}
                onChange={setRegionFilter}
                options={availableRegions.map((region) => ({ label: region, value: region }))}
                searchPlaceholder={t("chains.uptime.filter.searchRegions")}
                emptyText={t("chains.uptime.filter.noRegions")}
              />
            )}
            <FilterSelect
              label={t("chains.uptime.filter.threshold")}
              value={threshold}
              onChange={setThreshold}
              options={THRESHOLDS.map((value) => ({
                label: t("chains.uptime.filter.belowPercent", { percent: value }),
                value: String(value),
              }))}
              searchPlaceholder={t("chains.uptime.filter.threshold")}
              emptyText=""
            />
            {isFiltered && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={clearFilters}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </>
        }
      />
    </div>
  );
}

/**
 * The 24 hours of one store's day, as a single row of bands.
 *
 * The bands are flex children with no basis, so they divide the column evenly however
 * wide it ends up; `BAND_MIN_PX` is the floor that stops them collapsing, and the
 * container's matching min-width is what makes the table scroll instead. An hour with
 * nothing due is grey rather than red — it is an absence of schedule, not an outage —
 * and grey is the one colour here that follows the theme, because it belongs to the
 * surface rather than to the data.
 */
function UptimeStrip({ hours }: { hours: ChainUptimeStore["hours"] }) {
  const { t } = useTranslation("back-office");
  const [hoveredHour, setHoveredHour] = useState<number | null>(null);

  function describe(cell: ChainUptimeStore["hours"][number], uptime: number): string {
    return t("chains.uptime.cellTooltip", {
      hour: String(cell.hour).padStart(2, "0"),
      percent: formatUptimePercent(uptime),
      received: cell.imageCount,
      due: cell.dueCount,
    });
  }

  // Radix keeps the content mounted through its close animation. Reading the label and
  // the anchor position straight off `hoveredHour` therefore blanks them mid-exit — the
  // tooltip flashes as an empty box, and one parked back at hour 00 rather than where the
  // pointer actually was. Latch the last hour hovered and let the exit animate from there.
  const shown = useRef({ hour: 0, label: "" });
  const hovered = hoveredHour == null ? undefined : hours[hoveredHour];
  // An hour with nothing scheduled has nothing to report, so it gets no tooltip at all —
  // the legend already says what the grey means, and a box repeating it on every idle
  // band would fire across the whole overnight stretch of every row.
  const active = hovered?.uptime == null ? undefined : hovered;
  if (active?.uptime != null) shown.current = { hour: active.hour, label: describe(active, active.uptime) };

  return (
    <div
      className="relative flex h-8 items-stretch"
      style={{ minWidth: STRIP_MIN_PX, gap: BAND_GAP_PX }}
      onPointerLeave={() => setHoveredHour(null)}
    >
      {hours.map((cell, index) => (
        <span
          key={cell.hour}
          data-slot="uptime-cell"
          data-hour={cell.hour}
          {...(cell.uptime == null
            ? { "aria-hidden": true }
            : { role: "img", "aria-label": describe(cell, cell.uptime) })}
          onPointerEnter={() => setHoveredHour(index)}
          className={`flex-1 rounded-[2px] ${cell.uptime == null ? "bg-zinc-200 dark:bg-zinc-700" : ""}`}
          style={{
            minWidth: BAND_MIN_PX,
            background: cell.uptime == null ? undefined : uptimeSwatch(cell.uptime),
          }}
        />
      ))}
      {/*
        One tooltip for the whole strip rather than one per band. A chain of 500 stores is
        12,000 bands, and that many Radix roots is paid for on every sort, filter and
        keystroke — where a single controlled root per row costs 24x less. The trigger is an
        empty overlay parked on whichever band is hovered, so the tooltip still points at the
        hour it describes; it takes no pointer events of its own, leaving the bands to drive
        the hover.
      */}
      <Tooltip open={active != null}>
        <TooltipTrigger asChild>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0"
            style={{
              left: `${(shown.current.hour / HOURS.length) * 100}%`,
              width: `${100 / HOURS.length}%`,
            }}
          />
        </TooltipTrigger>
        <TooltipContent side="top">{shown.current.label}</TooltipContent>
      </Tooltip>
    </div>
  );
}

/**
 * Hour ticks above the strip.
 *
 * Built from the same flex rule as the bands so a label stays over the band it names at
 * every column width. Every slot is rendered even when it carries no text — dropping the
 * blank ones would let the remaining labels redistribute and drift off their hours.
 */
function UptimeAxis() {
  return (
    <div className="flex" style={{ minWidth: STRIP_MIN_PX, gap: BAND_GAP_PX }}>
      {HOURS.map((hour) => (
        <span
          key={hour}
          className="flex-1 overflow-hidden text-center font-normal text-[10px] text-muted-foreground tabular-nums"
          style={{ minWidth: BAND_MIN_PX }}
        >
          {hour % AXIS_LABEL_EVERY === 0 ? String(hour).padStart(2, "0") : ""}
        </span>
      ))}
    </div>
  );
}

/**
 * The ramp, spelled out. Without it a lone amber cell is ambiguous — the reader has no
 * way to tell whether the scale tops out at 1.0 or at the best value on screen.
 */
function UptimeLegend() {
  const { t } = useTranslation("back-office");
  return (
    <span className="inline-flex items-center gap-2 align-middle">
      <span className="text-xs">0%</span>
      <span
        className="h-3 w-24 rounded-full border border-border"
        style={{ backgroundImage: `linear-gradient(to right, ${UPTIME_GRADIENT})` }}
        aria-hidden
      />
      <span className="text-xs">100%</span>
      <span className="ml-1 h-3 w-3 rounded-[2px] bg-zinc-200 dark:bg-zinc-700" aria-hidden />
      <span className="text-xs">{t("chains.uptime.legendNotDue")}</span>
    </span>
  );
}
